/**
 * 顶层工作区（AppHeader 横向分页）——左栏菜单按工作区切分。
 *
 * 顶栏品牌「仿真平台」之后紧跟两个工作区入口：
 * - `simulation`    「SIL」软件在环：概览 / PNC 仿真 / 感知仿真 / 仿真场景 / 模型管理 /
 *   计划任务 / 系统
 * - `hil-stability`「HIL」硬件在环：HIL 仿真 / BVT 回灌 / 台架管理
 *
 * 当前工作区**不存独立状态**，一律从 pathname 反推：刷新、分享链接、直接粘 URL
 * 都会落在正确的工作区里；点分页 = 跳到该工作区的默认页。这样不会出现
 * "URL 是 /bvt/tasks 但分页停在 SIL" 这类不一致。
 */

export type WorkspaceKey = 'simulation' | 'hil-stability';

export interface WorkspaceSpec {
  key: WorkspaceKey;
  /** 分页文案 */
  label: string;
  /** 悬停说明：全称 + 包含的模块 */
  hint: string;
  /** 点该分页时落地的默认页 */
  defaultPath: string;
  /** 归属本工作区的路由前缀（整段匹配）；空数组 = 兜底工作区，不用前缀判定 */
  routePrefixes: readonly string[];
}

const WORKSPACE_SPECS: Record<WorkspaceKey, Omit<WorkspaceSpec, 'key'>> = {
  simulation: {
    label: 'SIL',
    hint: 'SIL 软件在环 · PNC / 感知 / 场景 / 模型',
    defaultPath: '/overview',
    routePrefixes: [],
  },
  'hil-stability': {
    label: 'HIL',
    hint: 'HIL 硬件在环 · HIL 仿真 / BVT 回灌 / 台架',
    defaultPath: '/hil/native/tasks',
    routePrefixes: ['/hil', '/bvt'],
  },
};

/** 分页展示顺序。 */
export const WORKSPACE_ORDER: readonly WorkspaceKey[] = ['simulation', 'hil-stability'];

export const WORKSPACES: readonly WorkspaceSpec[] = WORKSPACE_ORDER.map((key) => ({
  key,
  ...WORKSPACE_SPECS[key],
}));

/** 兜底工作区：pathname 不命中任何带前缀的工作区就归它。 */
export const DEFAULT_WORKSPACE_KEY: WorkspaceKey = 'simulation';

export const getWorkspace = (key: WorkspaceKey): WorkspaceSpec => ({
  key,
  ...WORKSPACE_SPECS[key],
});

/** 整段前缀匹配，避免 `/hilx` 误命中 `/hil`。 */
export const pathMatchesPrefix = (pathname: string, prefix: string): boolean =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

/** 由当前路由反推工作区。 */
export const resolveWorkspace = (pathname: string): WorkspaceKey => {
  const hit = WORKSPACE_ORDER.find((key) => {
    if (key === DEFAULT_WORKSPACE_KEY) return false;
    return WORKSPACE_SPECS[key].routePrefixes.some((prefix) => pathMatchesPrefix(pathname, prefix));
  });
  return hit ?? DEFAULT_WORKSPACE_KEY;
};

/**
 * 左栏顶层分组 key → 归属工作区。
 *
 * 分组定义在 Sidebar 里只有一份（保持图标唯一性 / 导航行为集中可查），
 * 这里只声明"哪个分组属于哪个工作区"，由 Sidebar 按 key 过滤。
 */
export const WORKSPACE_MENU_GROUPS: Record<WorkspaceKey, readonly string[]> = {
  simulation: [
    'overview',
    'planning',
    'perception',
    'scene',
    'model-management',
    'schedule',
    'system',
  ],
  // 台架管理（bench-directory）是顶层单项：同时覆盖 HIL / BVT 两种能力的台架，
  // 与 HIL 仿真、BVT 回灌并列，不归任何一个分组。
  'hil-stability': ['hil', 'bvt', 'bench-directory'],
};

export const isMenuKeyInWorkspace = (menuKey: string, workspace: WorkspaceKey): boolean =>
  WORKSPACE_MENU_GROUPS[workspace].includes(menuKey);
